/* ==========================================================================
   NeuroSockz — Variant selection

   A focused replacement for Dawn's product-info.js. Reads a variant map that
   Liquid has already serialised, so no price formatting or availability logic
   is duplicated in JavaScript — the strings come straight from the server.

   Publishes PUB_SUB_EVENTS.variantChange so other components (sticky bar,
   gallery) can react without coupling to this element.
   ========================================================================== */
(function () {
  'use strict';

  // Guarded so that loading this file twice (e.g. a section referenced from two
  // templates) can never throw "the name has already been used".
  const defineOnce = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  class NeuroVariants extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';

      const dataEl = this.querySelector('[data-variant-json]');
      try {
        this.variants = JSON.parse(dataEl?.textContent || '[]');
      } catch (e) {
        this.variants = [];
      }

      this.idInput = this.querySelector('input[name="id"]');
      this.inputs = Array.from(this.querySelectorAll('[data-option-input]'));
      if (!this.inputs.length) return;

      this.addEventListener('change', this.onChange.bind(this));
      // Sync the UI to whatever the server rendered as selected.
      this.updateAvailability();
    }

    get selectedOptions() {
      const values = [];
      this.inputs.forEach((input) => {
        if (input.type === 'radio') {
          if (input.checked) values[Number(input.dataset.optionIndex)] = input.value;
        } else {
          values[Number(input.dataset.optionIndex)] = input.value;
        }
      });
      return values;
    }

    findVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.findVariant(options);

      this.updateSelectedLabels();
      this.updateAvailability();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.currentVariant = variant;
      if (this.idInput) this.idInput.value = variant.id;

      this.updatePrice(variant);
      this.updateSku(variant);
      this.updateButton(variant);
      this.updateBrowserUrl(variant);
      this.updateGallery(variant);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, variant: variant },
        });
      }
    }

    /* Reflect the chosen value next to each option name */
    updateSelectedLabels() {
      this.querySelectorAll('[data-option-selected]').forEach((el) => {
        const index = el.dataset.optionSelected;
        const checked = this.querySelector(
          `[data-option-input][data-option-index="${index}"]:checked`
        );
        if (checked) el.textContent = checked.value;
      });
    }

    /* Grey out option values that produce no purchasable combination */
    updateAvailability() {
      const chosen = this.selectedOptions;

      this.inputs.forEach((input) => {
        if (input.type !== 'radio') return;
        const index = Number(input.dataset.optionIndex);
        const candidate = chosen.slice();
        candidate[index] = input.value;

        const match = this.variants.find((variant) =>
          variant.options.every((value, i) => (i === index ? value === input.value : value === candidate[i]))
        );

        const label = input.closest('.variant-option');
        const unavailable = !match || !match.available;
        if (label) label.classList.toggle('is-unavailable', unavailable);
        input.setAttribute('aria-describedby', unavailable ? `${input.id}-unavailable` : '');
      });
    }

    updatePrice(variant) {
      const target = document.getElementById(`Price-${this.sectionId}`);
      if (!target) return;
      // priceHtml is server-rendered Liquid, so currency and locale stay correct.
      if (variant.priceHtml) target.innerHTML = variant.priceHtml;
    }

    updateSku(variant) {
      const target = document.getElementById(`Sku-${this.sectionId}`);
      if (!target) return;
      target.textContent = variant.sku || '';
      target.classList.toggle('hidden', !variant.sku);
    }

    updateButton(variant) {
      const button = document.getElementById(`ProductSubmit-${this.sectionId}`);
      if (!button) return;
      const text = button.querySelector('[data-add-text]');

      button.disabled = !variant.available;
      if (text) {
        text.textContent = variant.available
          ? window.variantStrings.addToCart
          : window.variantStrings.soldOut;
      }

      document.querySelectorAll(`[data-sticky-price="${this.sectionId}"]`).forEach((el) => {
        if (variant.priceHtml) el.innerHTML = variant.priceHtml;
      });
    }

    setUnavailable() {
      const button = document.getElementById(`ProductSubmit-${this.sectionId}`);
      if (!button) return;
      const text = button.querySelector('[data-add-text]');
      button.disabled = true;
      if (text) text.textContent = window.variantStrings.unavailable;
    }

    updateBrowserUrl(variant) {
      if (!this.updateUrl || !this.productUrl) return;
      // replaceState rather than pushState: variant switching should not fill
      // the back button with intermediate states.
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }

    updateGallery(variant) {
      if (!variant.featured_media_id) return;
      document
        .querySelector(`neuro-gallery[data-section="${this.sectionId}"]`)
        ?.selectMedia?.(variant.featured_media_id);
    }
  }

  defineOnce('neuro-variants', NeuroVariants);
})();
